<template>
  <div v-if="hasOptions">
    <!-- Accompaniments Section -->
    <div v-if="hasAccompaniment" style="scroll-margin: 50px">
      <div class="bg-slate-100 px-6 py-2 flex gap-2 text-sm">
        <div class="truncate text-gray-800">Accompagnements</div>
      </div>
      <div class="flex flex-col py-4">
        <div
          v-for="(choice, i) in accompaniments"
          :key="i"
          class="flex gap-2 px-6 py-4 items-center relative"
        >
          <div
            class="absolute left-0 top-0 w-full h-full pointer-events-none z-[1]"
          >
            <div
              class="w-full h-full ripple"
              :class="{ '!pointer-events-auto': true }"
              @click="accompanimentClick(choice)"
            ></div>
          </div>
          <div
            class="duration-300 w-10 h-10 border text-lg items-center justify-center flex relative"
            :class="{
              'border-slate-200 rounded-xl [&_.checkIcon]:opacity-0':
                !isChoiceSelected(choice.id),
              'border-primary-500 bg-primary-500 rounded-xl [&_.checkIcon]:opacity-100':
                isChoiceSelected(choice.id)
            }"
          >
            <div class="checkIcon duration-300 absolute w-6 h-6">
              <ImgComponent
                src="/images/all/check-normal-white.svg"
                alt=""
                class="w-full h-full object-contain"
              />
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="truncate text-gray-800">
              {{ choice.name }}
            </div>
          </div>
          <div v-if="choice.amount" class="ml-auto">
            <div
              :class="{
                'text-gray-800 font-bold': isChoiceSelected(choice.id)
              }"
            >
              <!-- {{ totalChoiceAmount(choice) }} FCFA -->
            </div>
            <div
              :class="{
                'text-gray-800 font-bold': isChoiceSelected(choice.id)
              }"
            >
              <!-- {{ choice.quantity }} &times; -->
              {{ choice.amount }} FCFA
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Options Sections -->
    <div
      v-for="(option, i) in options"
      :key="i"
      :id="`optionSection-${option.id}`"
      style="scroll-margin: 50px"
    >
      <div class="bg-slate-100 px-6 py-2 flex gap-2 text-sm">
        <div
          class="truncate text-gray-800"
          :class="{
            'max-w-[calc(100%-10ch)]': option.required
          }"
        >
          {{ option.name }}
        </div>
        <div>
          {{ option.maxChoices ? 'max : ' + option.maxChoices : '' }}
        </div>
        <div class="ml-auto font-bold">
          {{ option.required ? 'Requis*' : '' }}
        </div>
      </div>
      <div class="flex flex-col py-4">
        <div v-for="(choice, j) in option.choices" :key="j">
          <div class="flex gap-2 px-6 py-4 items-center relative">
            <div
              class="absolute left-0 top-0 w-full h-full pointer-events-none z-[1]"
            >
              <div
                class="w-full h-full ripple"
                :class="{
                  '!pointer-events-auto':
                    !isChoiceSelected(choice.id) || choice.max === 1
                }"
                @click="choiceClick(choice, option)"
              ></div>
            </div>

            <!-- Decrease button for multi-select -->
            <div
              class="overflow-hidden duration-500 -mr-2 rounded-lg opacity-0 w-0 h-10 text-lg border border-[#F4F4F5] bg-[#F4F4F5] items-center justify-center flex"
              @click.stop="decreaseOptionSelection(choice, option)"
              :class="{
                '!w-10 !opacity-100 !mr-0':
                  choiceSelectionData(choice.id).number > 0 &&
                  choiceSelectionData(choice.id).max > 1,
                ripple:
                  choiceSelectionData(choice.id).number > 0 &&
                  choiceSelectionData(choice.id).max > 1
              }"
            >
              <div>-</div>
            </div>

            <!-- Quantity display -->
            <div
              class="text-gray-800 overflow-hidden duration-500 w-0 h-8 -mr-2 opacity-0 text-lg items-center justify-center flex"
              :class="{
                '!w-6 !opacity-100 !mr-0':
                  choiceSelectionData(choice.id).number > 0 &&
                  choiceSelectionData(choice.id).max > 1
              }"
            >
              <div>{{ choiceSelectionData(choice.id).number || 0 }}</div>
            </div>

            <!-- Add/Check button -->
            <div
              class="duration-300 w-10 h-10 border text-lg items-center justify-center flex relative"
              :class="{
                'border-slate-200 rounded-xl [&_.plusIcon]:opacity-100 [&_.checkIcon]:opacity-0 ripple':
                  !isChoiceSelected(choice.id),
                'border-[#F4F4F5] bg-[#F4F4F5] rounded-lg [&_.checkIcon]:opacity-0 [&_.plusIcon]:opacity-100 ripple':
                  isChoiceSelected(choice.id) && choice.max > 1,
                'border-primary-500 bg-primary-500 rounded-xl [&_.checkIcon]:opacity-100 [&_.plusIcon]:opacity-0':
                  isChoiceSelected(choice.id) && choice.max === 1
              }"
              @click.stop="increaseOptionSelection(choice, option)"
            >
              <div class="plusIcon duration-300 absolute">+</div>
              <div class="checkIcon duration-300 absolute w-6 h-6">
                <ImgComponent
                  src="/images/all/check-normal-white.svg"
                  alt=""
                  class="w-full h-full object-contain"
                />
              </div>
            </div>

            <div class="flex-1 min-w-0">
              <div class="truncate text-gray-800">
                {{ choice.name }}
              </div>
            </div>

            <div v-if="choice.amount" class="ml-auto text-right">
              <div
                :class="{
                  'text-gray-800 font-bold text-lg': isChoiceSelected(choice.id)
                }"
              >
                {{ totalChoiceAmount(choice) || choice.amount }} FCFA
              </div>
              <div
                class="duration-300 overflow-hidden max-h-lh"
                :class="{
                  'max-h-0!': choiceSelectionData(choice.id).number <= 1,
                  'text-sm': isChoiceSelected(choice.id)
                }"
              >
                {{ choiceSelectionData(choice.id).number }} &times;
                {{ choice.amount }} FCFA
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import ImgComponent from '@ui/core/img/ImgComponent.global.vue';

const props = defineProps({
  dishData: {
    type: Object,
    required: true
  },
  selectedOptions: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:options']);

const selectedChoices = ref({});

// Computed
const hasOptions = computed(() => {
  return props.dishData?.options && props.dishData.options.length > 0;
});

const hasAccompaniment = computed(() => {
  return (
    props.dishData?.accompaniments && props.dishData.accompaniments.length > 0
  );
});

const accompaniments = computed(() => {
  return props.dishData?.accompaniments || [];
});

const options = computed(() => {
  return props.dishData?.options || [];
});

// Methods
const isChoiceSelected = (choiceId) => {
  return selectedChoices.value[choiceId]?.number > 0;
};

const choiceSelectionData = (choiceId) => {
  return selectedChoices.value[choiceId] || { number: 0, max: 1 };
};

const totalChoiceAmount = (choice) => {
  const quantity = choiceSelectionData(choice.id).number;
  return (choice.amount || 0) * quantity;
};

const accompanimentClick = (choice) => {
  const current = choiceSelectionData(choice.id);

  if (current.number > 0) {
    // Deselect
    delete selectedChoices.value[choice.id];
  } else {
    // Select accompaniment (single choice, max 1)
    // Clear other accompaniments first
    accompaniments.value.forEach((acc) => {
      if (acc.id !== choice.id) {
        delete selectedChoices.value[acc.id];
      }
    });
    selectedChoices.value[choice.id] = {
      number: 1,
      max: 1,
      choice,
      optionId: 'accompaniments' // Special ID for accompaniments group
    };
  }

  emitOptions();
};

const choiceClick = (choice, option) => {
  if (!isChoiceSelected(choice.id)) {
    increaseOptionSelection(choice, option);
  } else if (option.max === 1) {
    // Toggle off for single choice
    decreaseOptionSelection(choice, option);
  }
};

const increaseOptionSelection = (choice, option) => {
  const current = choiceSelectionData(choice.id);
  const max = choice.max || option.max || 1;

  if (current.number >= max) {
    return;
  }

  // Check if we need to enforce max choices for the option
  if (option.maxChoices) {
    const optionTotal = Object.values(selectedChoices.value)
      .filter((s) => s.optionId === option.id)
      .reduce((sum, s) => sum + s.number, 0);

    if (optionTotal >= option.maxChoices) {
      return;
    }
  }

  selectedChoices.value[choice.id] = {
    number: current.number + 1,
    max,
    choice,
    optionId: option.id
  };

  emitOptions();
};

const decreaseOptionSelection = (choice, option) => {
  const current = choiceSelectionData(choice.id);

  if (current.number <= 0) {
    return;
  }

  if (current.number === 1) {
    delete selectedChoices.value[choice.id];
  } else {
    selectedChoices.value[choice.id] = {
      ...current,
      number: current.number - 1
    };
  }

  emitOptions();
};

const emitOptions = () => {
  // Convert selectedChoices object to array format expected by API
  const optionsArray = Object.values(selectedChoices.value).map((selection) => {
    const { choice, optionId, number: quantity } = selection;
    const price = choice.amount || choice.price || 0;

    // Determine optionName based on whether it's an accompaniment or regular option
    let optionName = '';
    if (optionId === 'accompaniments') {
      optionName = 'Accompagnements';
    } else {
      // Find the option object to get its name
      const option = options.value.find((opt) => opt.id === optionId);
      optionName =
        option?.name || choice.optionName || choice.optionGroupName || '';
    }

    return {
      optionId: optionId || choice.optionId || '',
      optionName: optionName,
      valueId: choice.id || choice.valueId || '',
      valueName: choice.name || choice.valueName || '',
      price: price,
      quantity: quantity,
      total: price * quantity
    };
  });

  emit('update:options', optionsArray);
};

// Initialize selectedChoices from selectedOptions prop
watch(
  () => props.selectedOptions,
  (newOptions) => {
    console.log('[DishOptionsChoices] selectedOptions changed:', newOptions);

    if (!newOptions || newOptions.length === 0) {
      selectedChoices.value = {};
      return;
    }

    // Build selectedChoices by matching with available options
    const initialized = {};

    // Get all options (regular + accompaniments)
    const availableOptions = props.dishData?.options || [];
    const accompaniments = props.dishData?.accompaniments || [];

    console.log('[DishOptionsChoices] availableOptions:', availableOptions);
    console.log('[DishOptionsChoices] accompaniments:', accompaniments);

    // Match selectedOptions with available options
    newOptions.forEach((selectedOpt) => {
      const optionId = selectedOpt.optionId;
      const choiceId = selectedOpt.choiceId || selectedOpt.valueId;
      const quantity = selectedOpt.quantity || 1;

      console.log('[DishOptionsChoices] Processing selectedOpt:', selectedOpt);
      console.log('[DishOptionsChoices] Extracted:', {
        optionId,
        choiceId,
        quantity
      });

      // Find in regular options
      const option = availableOptions.find((opt) => opt.id === optionId);
      if (option) {
        const choice = option.choices?.find((c) => c.id === choiceId);
        if (choice) {
          initialized[choice.id] = {
            number: quantity,
            max: choice.max || option.max || 1,
            choice: choice,
            optionId: option.id
          };
          console.log(
            '[DishOptionsChoices] Initialized choice:',
            initialized[choice.id]
          );
        }
      }

      // Find in accompaniments
      if (optionId === 'accompaniments') {
        const acc = accompaniments.find((a) => a.id === choiceId);
        if (acc) {
          initialized[acc.id] = {
            number: quantity,
            max: 1,
            choice: acc,
            optionId: 'accompaniments'
          };
          console.log(
            '[DishOptionsChoices] Initialized accompaniment:',
            initialized[acc.id]
          );
        }
      }
    });

    console.log('[DishOptionsChoices] Final initialized:', initialized);

    // Only update if different to avoid infinite loop
    if (JSON.stringify(selectedChoices.value) !== JSON.stringify(initialized)) {
      selectedChoices.value = initialized;
    }
  },
  { immediate: true, deep: true }
);
</script>
